import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2, 
  CalendarDays, 
  CheckCircle2, 
  MapPin, 
  Phone, 
  Instagram, 
  Save, 
  UploadCloud, 
  Clock, 
  Coins, 
  Bell, 
  BellRing, 
  Sparkles, 
  ChevronRight, 
  ChevronLeft, 
  X, 
  ShieldCheck, 
  Image as ImageIcon,
  Trash2,
  Plus,
  Check
} from 'lucide-react';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { storage, getCollectionPath } from '../../lib/firebase';
import { UserProfile, Config, DaySchedule, TimeDiscount } from '../../types';
import { ALL_CITIES, PROVINCES, CITIES_BY_PROVINCE } from '../../data/locations';

interface AdminOnboardingModalProps {
  user: { uid: string; email: string | null; displayName: string | null };
  db: any;
  config: Config;
  onComplete: (updatedProfile: UserProfile) => void;
}

export function AdminOnboardingModal({ user, db, config, onComplete }: AdminOnboardingModalProps) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stepError, setStepError] = useState('');

  // Step 1: Personalización / Identidad del Club
  const [name, setName] = useState(user.displayName || '');
  const [phone, setPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [address, setAddress] = useState('');
  const [province, setProvince] = useState('RN'); // Default to RN for now
  const [city, setCity] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [coverUrl, setCoverUrl] = useState('');

  // Step 2: Ajustes de Reserva
  const [courtsCount, setCourtsCount] = useState(3);
  const [openTime, setOpenTime] = useState('09:30');
  const [closeTime, setCloseTime] = useState('00:30');
  const [slotDuration, setSlotDuration] = useState(90);
  const [price, setPrice] = useState(48000);
  const [priceInput, setPriceInput] = useState('48.000');

  // Step 3: Horarios específicos por Día de la semana
  const [specificDaySchedules, setSpecificDaySchedules] = useState<DaySchedule[]>([
    { dayOfWeek: 1, openTime: '09:30', closeTime: '00:30', closed: false }, // Lunes
    { dayOfWeek: 2, openTime: '09:30', closeTime: '00:30', closed: false }, // Martes
    { dayOfWeek: 3, openTime: '09:30', closeTime: '00:30', closed: false }, // Miércoles
    { dayOfWeek: 4, openTime: '09:30', closeTime: '00:30', closed: false }, // Jueves
    { dayOfWeek: 5, openTime: '09:30', closeTime: '00:30', closed: false }, // Viernes
    { dayOfWeek: 6, openTime: '09:30', closeTime: '00:30', closed: false }, // Sábado
    { dayOfWeek: 0, openTime: '09:30', closeTime: '00:30', closed: false }, // Domingo
  ]);

  const handleOpenTimeChange = (newVal: string) => {
    const oldVal = openTime;
    setOpenTime(newVal);
    setSpecificDaySchedules(prev => prev.map(s => s.openTime === oldVal ? { ...s, openTime: newVal } : s));
  };

  const handleCloseTimeChange = (newVal: string) => {
    const oldVal = closeTime;
    setCloseTime(newVal);
    setSpecificDaySchedules(prev => prev.map(s => s.closeTime === oldVal ? { ...s, closeTime: newVal } : s));
  };

  // Step 4: Horarios con Descuento
  const [timeDiscounts, setTimeDiscounts] = useState<TimeDiscount[]>([]);
  const [discDays, setDiscDays] = useState<number[]>([]);
  const [discStart, setDiscStart] = useState('12:00');
  const [discEnd, setDiscEnd] = useState('16:00');
  const [discType, setDiscType] = useState<'percentage' | 'fixed'>('percentage');
  const [discVal, setDiscVal] = useState(20);
  const [discCourts, setDiscCourts] = useState<string[]>([]);
  const [showAddDiscount, setShowAddDiscount] = useState(false);

  // Step 5: Notificaciones
  const [enableNotifications, setEnableNotifications] = useState(true);

  // Client-side image compression helper
  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Compress as WEBP to preserve transparency
          const dataUrl = canvas.toDataURL('image/webp', 0.8);
          resolve(dataUrl);
        };
        img.onerror = (err) => reject(err);
      };
      reader.onerror = (err) => reject(err);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'photoUrl' | 'coverUrl') => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressedBase64 = await compressImage(file);
      if (field === 'photoUrl') {
        setPhotoUrl(compressedBase64);
      } else if (field === 'coverUrl') {
        setCoverUrl(compressedBase64);
      }
    } catch (err) {
      console.error(err);
      setStepError('Error al procesar la imagen.');
    }
  };

  const handlePriceChange = (val: string) => {
    // Keep only digits
    const digits = val.replace(/\D/g, '');
    const numericValue = digits ? parseInt(digits, 10) : 0;
    setPrice(numericValue);
    setPriceInput(numericValue ? numericValue.toLocaleString('es-AR') : '');
  };

  const handleSubmit = async (activateNotifications: boolean) => {
    setLoading(true);
    setStepError('');

    try {
      let finalPhotoUrl = photoUrl;
      let finalCoverUrl = coverUrl;
      
      if (finalPhotoUrl.startsWith('data:image/')) {
        const imageRef = ref(storage, `users/${user.uid}_photo_${Date.now()}.jpg`);
        await uploadString(imageRef, finalPhotoUrl, 'data_url');
        finalPhotoUrl = await getDownloadURL(imageRef);
      }
      
      if (finalCoverUrl.startsWith('data:image/')) {
        const imageRef = ref(storage, `users/${user.uid}_cover_${Date.now()}.jpg`);
        await uploadString(imageRef, finalCoverUrl, 'data_url');
        finalCoverUrl = await getDownloadURL(imageRef);
      }

      // 1. Prepare UserProfile updates
      const updatedProfile: Partial<UserProfile> = {
        uid: user.uid,
        email: user.email || '',
        name: name,
        phone: phone,
        instagram: instagram,
        address: address,
        city: city,
        location: province,
        photoUrl: finalPhotoUrl,
        coverUrl: finalCoverUrl,
        courtsCount: courtsCount,
        isAdminOnboardingComplete: true,
        updatedAt: Date.now()
      };

      // 2. Request FCM permission if selected
      if (activateNotifications) {
        try {
          const { requestNotificationPermission } = await import('../../lib/notifications');
          const token = await requestNotificationPermission(user.uid);
          if (typeof token === 'string' && token) {
            updatedProfile.notificationsEnabled = true;
            if (token !== "PENDING_TOKEN") {
              if (!updatedProfile.fcmTokens) updatedProfile.fcmTokens = [];
              if (!updatedProfile.fcmTokens.includes(token)) {
                updatedProfile.fcmTokens.push(token);
              }
            }
          }
        } catch (error) {
          console.error("Error obtaining FCM token:", error);
          // Don't block onboarding if notifications fail
        }
      }

      // 3. Save to `users/{userId}`
      const userDocRef = doc(db, getCollectionPath('users'), user.uid);
      await updateDoc(userDocRef, updatedProfile);

      // Filter customized day-by-day schedules (e.g. if day is closed or hours differ from general default)
      const finalSpecificSchedules = specificDaySchedules.filter(s => {
        return s.closed || s.openTime !== openTime || s.closeTime !== closeTime;
      });

      // 4. Save to `clubSchedules/{userId}`
      const scheduleRef = doc(db, getCollectionPath('clubSchedules'), user.uid);
      await setDoc(scheduleRef, {
        id: user.uid,
        courts: Array.from({ length: courtsCount }, (_, i) => `Cancha ${i + 1}`),
        openTime: openTime,
        closeTime: closeTime,
        slotDuration: slotDuration,
        pricePerSlot: Number(price),
        updatedAt: Date.now(),
        specificDaySchedules: finalSpecificSchedules,
        timeDiscounts: timeDiscounts,
        offDays: []
      });

      // 5. Trigger success callback
      onComplete(updatedProfile as UserProfile);

    } catch (err: any) {
      console.error("Error inside AdminOnboardingModal:", err);
      setStepError('Ocurrió un error al guardar tu configuración. Reintenta.');
    } finally {
      setLoading(false);
    }
  };

  const glowVariants = {
    animate1: {
      x: ['-35%', '45%', '-20%', '-35%'],
      y: ['-20%', '35%', '55%', '-20%'],
      scale: [1, 1.3, 0.8, 1],
      transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' }
    },
    animate2: {
      x: ['35%', '-35%', '20%', '35%'],
      y: ['45%', '-20%', '-45%', '45%'],
      scale: [1.2, 0.7, 1.3, 1.2],
      transition: { duration: 10, repeat: Infinity, ease: 'easeInOut' }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      
      {/* Background & Ambient Effects */}
      <div className="absolute inset-0 z-0 bg-[#030712] overflow-hidden">
        {config.bgUrl && (
          <div
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-700 ${config.bgBlur ? 'blur-sm scale-105' : ''}`}
            style={{ backgroundImage: `url(${config.bgUrl})` }}
          />
        )}
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-md" />

        {/* Ambient Moving Lights */}
        <motion.div
          variants={glowVariants}
          animate="animate1"
          className="absolute -top-[10%] -left-[10%] w-[45%] h-[45%] rounded-full bg-gradient-to-br from-lime-500/25 via-emerald-500/12 to-transparent blur-[80px] pointer-events-none"
        />
        <motion.div
          variants={glowVariants}
          animate="animate2"
          className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] rounded-full bg-gradient-to-tr from-cyan-500/25 via-blue-500/12 to-transparent blur-[90px] pointer-events-none"
        />
        
        <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.01)_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none opacity-40" />
      </div>

      {/* Main Container Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative z-10 w-full max-w-lg bg-[#11131c]/90 backdrop-blur-3xl border border-white/[0.12] rounded-[2.5rem] shadow-[0_30px_75px_rgba(0,0,0,0.65)] overflow-hidden flex flex-col max-h-[95vh]"
      >
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-white/5 overflow-hidden">
          <motion.div
            className="h-full theme-primary-bg"
            initial={{ width: '20%' }}
            animate={{ width: `${(step / 5) * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>

        {/* Header */}
        <div className="p-6 pb-2 text-center mt-2">
          <div className="flex flex-col items-center mb-4">
            {config.logoUrl ? (
              <img src={config.logoUrl} alt="Logo" className="h-10 w-10 mb-2 object-contain" />
            ) : (
              <div className="h-10 w-10 mb-2 rounded-xl bg-white/5 flex items-center justify-center">
                <ShieldCheck size={24} className="theme-primary-text" />
              </div>
            )}
            <h1 className="text-xl sm:text-2xl font-black uppercase italic theme-primary-text tracking-tight leading-none">
              Configuración Inicial
            </h1>
            <p className="text-[10px] opacity-60 uppercase tracking-widest font-bold text-white/70 mt-1">
              Onboarding de Administrador
            </p>
          </div>

          <div className="flex justify-center gap-1.5 mb-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`h-1 w-8 rounded-full transition-all duration-500 ${s <= step ? 'theme-primary-bg' : 'bg-white/10'}`}
              />
            ))}
          </div>
        </div>

        {/* Form Body - Custom Scrollbar */}
        <div className="flex-1 overflow-y-auto px-8 pb-6 custom-scrollbar space-y-5">
          <AnimatePresence mode="wait">
            
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center pb-2">
                  <h3 className="text-xs font-black uppercase tracking-wider theme-primary-text flex items-center justify-center gap-1.5">
                    <Building2 size={14} /> Identidad del Club
                  </h3>
                  <p className="text-[10px] text-zinc-400 mt-1">Completa los datos públicos que verán los jugadores para contactarte.</p>
                </div>

                {/* Logo and Cover Side-by-Side uploads */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Logo (Square) */}
                  <div className="flex flex-col items-center p-3 bg-white/5 border border-white/10 rounded-2xl relative">
                    <span className="text-[9px] font-black uppercase opacity-60 mb-2 text-white">Logo Club</span>
                    <div className="w-20 h-20 rounded-full border-2 border-white/20 overflow-hidden relative group bg-black/40 flex items-center justify-center">
                      {photoUrl ? (
                        <img src={photoUrl} alt="Logo" className="w-full h-full object-cover" />
                      ) : (
                        <UploadCloud size={24} className="opacity-40 text-theme-primary" />
                      )}
                      <label className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer bg-black/70 transition-all text-[8px] font-bold uppercase tracking-widest text-center px-1">
                        Subir
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'photoUrl')} />
                      </label>
                    </div>
                  </div>

                  {/* Cover (Horizontal) */}
                  <div className="flex flex-col items-center p-3 bg-white/5 border border-white/10 rounded-2xl relative">
                    <span className="text-[9px] font-black uppercase opacity-60 mb-2 text-white">Foto Portada (Opcional)</span>
                    <div className="w-full h-20 rounded-xl border border-white/10 overflow-hidden relative group bg-black/40 flex items-center justify-center aspect-[16/9]">
                      {coverUrl ? (
                        <img src={coverUrl} alt="Portada" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={24} className="opacity-40 text-theme-primary" />
                      )}
                      <label className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer bg-black/70 transition-all text-[8px] font-bold uppercase tracking-widest text-center px-1">
                        Subir
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'coverUrl')} />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-1">
                  <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 ml-1">Nombre del Club *</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej: Patagonia Pádel Arena"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 focus:border-theme-primary/40 outline-none text-white font-bold text-sm transition-all"
                  />
                </div>

                {/* Phone & Instagram */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 ml-1">Teléfono</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30 text-zinc-400" size={14} />
                      <input
                        type="text"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Nº de contacto"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-3 focus:border-theme-primary/40 outline-none text-white font-bold text-xs transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 ml-1">Instagram</label>
                    <div className="relative flex items-center">
                      <span className="absolute left-3.5 text-xs font-black text-white/65 select-none">
                        @
                      </span>
                      <input
                        type="text"
                        value={instagram}
                        placeholder="tu_usuario"
                        onChange={(e) => {
                          let val = e.target.value.trim();
                          if (val.startsWith('@')) {
                            val = val.substring(1);
                          }
                          if (val.includes('instagram.com/')) {
                            const parts = val.split('instagram.com/');
                            if (parts.length > 1) {
                              val = parts[1].split('/')[0].split('?')[0];
                            }
                          }
                          setInstagram(val);
                        }}
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-3 focus:border-theme-primary/40 outline-none text-white font-bold text-xs transition-all placeholder:text-zinc-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Province, City & Address */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 ml-1">Provincia *</label>
                    <select
                      value={province}
                      onChange={(e) => {
                        setProvince(e.target.value);
                        setCity('');
                      }}
                      className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-3 focus:border-theme-primary/40 outline-none text-white font-bold text-xs cursor-pointer transition-all"
                    >
                      <option value="">Seleccionar...</option>
                      {PROVINCES.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 ml-1">Ciudad *</label>
                    <select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      disabled={!province}
                      className="w-full bg-zinc-900 border border-white/10 rounded-xl px-3 py-3 focus:border-theme-primary/40 outline-none text-white font-bold text-xs cursor-pointer transition-all disabled:opacity-50"
                    >
                      <option value="">Seleccionar...</option>
                      {(CITIES_BY_PROVINCE[province as keyof typeof CITIES_BY_PROVINCE] || []).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 ml-1">Dirección Exacta</label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30 text-zinc-400" size={14} />
                      <input
                        type="text"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Calle, Número"
                        className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-3 focus:border-theme-primary/40 outline-none text-white font-bold text-xs transition-all"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center pb-2">
                  <h3 className="text-xs font-black uppercase tracking-wider theme-primary-text flex items-center justify-center gap-1.5">
                    <CalendarDays size={14} /> Ajustes de Reservas y Canchas
                  </h3>
                </div>

                {/* Courts Selector (1-6) */}
                <div className="space-y-2 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-white/60 text-center">Nº de Canchas Disponibles</label>
                  <div className="grid grid-cols-6 gap-2">
                    {[1, 2, 3, 4, 5, 6].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setCourtsCount(num)}
                        className={`py-2.5 rounded-xl border font-black text-sm transition-all ${
                          courtsCount === num 
                            ? 'theme-primary-bg text-black border-transparent scale-105 shadow-md' 
                            : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hours Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1 flex flex-col items-center p-3 bg-white/5 rounded-xl border border-white/10">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 text-center">Hora Apertura</label>
                    <input
                      type="time"
                      value={openTime}
                      onChange={(e) => handleOpenTimeChange(e.target.value)}
                      style={{ colorScheme: 'dark' }}
                      className="bg-[#1a1c23] border border-white/10 rounded-lg px-2.5 py-1.5 text-white font-bold text-center text-xs focus:border-theme-primary/40 outline-none w-full max-w-[110px] mt-1 transition-all"
                    />
                    <span className="text-[8px] text-zinc-400/80 mt-1 text-center font-medium">Inicio del primer turno</span>
                  </div>
                  <div className="space-y-1 flex flex-col items-center p-3 bg-white/5 rounded-xl border border-white/10">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 text-center">Hora Cierre</label>
                    <input
                      type="time"
                      value={closeTime}
                      onChange={(e) => handleCloseTimeChange(e.target.value)}
                      style={{ colorScheme: 'dark' }}
                      className="bg-[#1a1c23] border border-white/10 rounded-lg px-2.5 py-1.5 text-white font-bold text-center text-xs focus:border-theme-primary/40 outline-none w-full max-w-[110px] mt-1 transition-all"
                    />
                    <span className="text-[8px] text-zinc-400/80 mt-1 text-center font-medium">Finalización del último turno</span>
                  </div>
                </div>

                {/* Slot Duration & Turn price */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1 flex flex-col items-center p-3 bg-white/5 rounded-xl border border-white/10">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 text-center">Duración Turno</label>
                    <select
                      value={slotDuration}
                      onChange={(e) => setSlotDuration(Number(e.target.value))}
                      className="bg-[#11131c] border border-white/10 rounded-lg px-2 py-1.5 text-white font-bold text-center text-xs focus:border-theme-primary/40 outline-none w-full max-w-[120px] mt-1 cursor-pointer"
                    >
                      <option value={60}>60 mins</option>
                      <option value={90}>90 mins</option>
                      <option value={120}>120 mins</option>
                    </select>
                  </div>
                  <div className="space-y-1 flex flex-col items-center p-3 bg-white/5 rounded-xl border border-white/10">
                    <label className="block text-[9px] font-black uppercase tracking-wider text-zinc-400 text-center">Precio total por cancha ($)</label>
                    <div className="relative mt-1 w-full max-w-[120px]">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40 text-[10px] font-bold">$</span>
                      <input
                        type="text"
                        value={priceInput}
                        onChange={(e) => handlePriceChange(e.target.value)}
                        className="bg-[#11131c] border border-white/10 rounded-lg pl-6 pr-2 py-1.5 text-white font-bold text-center text-xs focus:border-theme-primary/40 outline-none w-full"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center pb-2">
                  <h3 className="text-xs font-black uppercase tracking-wider theme-primary-text flex items-center justify-center gap-1.5">
                    <CalendarDays size={14} /> Horarios por Día de la Semana
                  </h3>
                  <p className="text-[10px] text-zinc-400 mt-1 font-medium">Configura si el club abre o cierra cada día, o si tiene un horario especial.</p>
                </div>

                <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-1 bg-white/5 p-3 rounded-2xl border border-white/10 custom-scrollbar">
                  {[
                    { dayOfWeek: 1, label: 'Lunes' },
                    { dayOfWeek: 2, label: 'Martes' },
                    { dayOfWeek: 3, label: 'Miércoles' },
                    { dayOfWeek: 4, label: 'Jueves' },
                    { dayOfWeek: 5, label: 'Viernes' },
                    { dayOfWeek: 6, label: 'Sábado' },
                    { dayOfWeek: 0, label: 'Domingo' }
                  ].map(({ dayOfWeek, label }) => {
                    const daySched = specificDaySchedules.find(s => s.dayOfWeek === dayOfWeek) || {
                      dayOfWeek,
                      openTime: openTime,
                      closeTime: closeTime,
                      closed: false
                    };

                    return (
                      <div key={dayOfWeek} className="flex items-center justify-between gap-2 p-2 bg-[#11131c]/60 rounded-xl border border-white/5">
                        <span className="text-[11px] font-black text-white/90 min-w-[70px] text-left">{label}</span>
                        
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setSpecificDaySchedules(prev => prev.map(s => s.dayOfWeek === dayOfWeek ? { ...s, closed: !s.closed } : s));
                            }}
                            className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all duration-300 ${
                              daySched.closed
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            }`}
                          >
                            {daySched.closed ? 'Cerrado' : 'Abierto'}
                          </button>

                          {!daySched.closed && (
                            <div className="flex items-center gap-1.5">
                              <input
                                type="time"
                                value={daySched.openTime}
                                onChange={(e) => {
                                  setSpecificDaySchedules(prev => prev.map(s => s.dayOfWeek === dayOfWeek ? { ...s, openTime: e.target.value } : s));
                                }}
                                style={{ colorScheme: 'dark' }}
                                className="bg-[#1a1c23] border border-white/10 rounded-lg px-2 py-1 text-white font-bold text-[11px] text-center outline-none w-[84px] focus:border-theme-primary/40 transition-all"
                              />
                              <span className="text-white/40 text-[9px] font-black">-</span>
                              <input
                                type="time"
                                value={daySched.closeTime}
                                onChange={(e) => {
                                  setSpecificDaySchedules(prev => prev.map(s => s.dayOfWeek === dayOfWeek ? { ...s, closeTime: e.target.value } : s));
                                }}
                                style={{ colorScheme: 'dark' }}
                                className="bg-[#1a1c23] border border-white/10 rounded-lg px-2 py-1 text-white font-bold text-[11px] text-center outline-none w-[84px] focus:border-theme-primary/40 transition-all"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-left">
                  <p className="text-[9px] text-blue-400 font-medium leading-relaxed flex items-start gap-1.5">
                    <span className="text-xs leading-none">💡</span>
                    <span>
                      <strong>Paso Omitible:</strong> Si el club abre en el horario general ({openTime} a {closeTime}) todos los días, puedes dejar todo como está y continuar. Puedes modificarlo cuando quieras en Ajustes.
                    </span>
                  </p>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="text-center pb-2">
                  <h3 className="text-xs font-black uppercase tracking-wider theme-primary-text flex items-center justify-center gap-1.5">
                    <Coins size={14} /> Horarios con Descuento
                  </h3>
                  <p className="text-[10px] text-zinc-400 mt-1 font-medium">Ofrece tarifas promocionales para atraer más jugadores en horas de baja demanda.</p>
                </div>

                {/* Warning/Skip Banner */}
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 text-left">
                  <p className="text-[9px] text-blue-400 font-medium leading-relaxed flex items-start gap-1.5">
                    <span className="text-xs leading-none">💡</span>
                    <span>
                      <strong>Paso Omitible:</strong> No es obligatorio configurar descuentos ahora. Haz clic en Siguiente si prefieres configurarlos más adelante desde tu panel de Ajustes.
                    </span>
                  </p>
                </div>

                {/* Discount List */}
                <div className="space-y-2">
                  {timeDiscounts.length === 0 ? (
                    <div className="text-center p-4 bg-white/5 rounded-2xl border border-white/10 border-dashed">
                      <p className="text-[9px] text-zinc-500 uppercase tracking-widest font-black">No hay descuentos agregados aún</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[20vh] overflow-y-auto pr-1 custom-scrollbar">
                      {timeDiscounts.map((discount) => {
                        const daysStr = discount.daysOfWeek
                          .map(d => ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'][d])
                          .join(', ');
                        return (
                          <div key={discount.id} className="flex items-center justify-between p-2.5 bg-white/5 rounded-xl border border-white/10">
                            <div className="space-y-0.5 text-left">
                              <p className="text-xs font-black text-white">
                                {discount.discountType === 'percentage' ? `${discount.value}% OFF` : `Bonif. $${discount.value.toLocaleString('es-AR')}`}
                              </p>
                              <p className="text-[9px] text-zinc-400 font-medium">
                                {daysStr} • {discount.startHour} a {discount.endHour}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setTimeDiscounts(timeDiscounts.filter(d => d.id !== discount.id))}
                              className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-all"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Add Discount Form Inline Toggle */}
                {!showAddDiscount ? (
                  <button
                    type="button"
                    onClick={() => setShowAddDiscount(true)}
                    className="w-full py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-wider text-white transition-all flex items-center justify-center gap-1.5"
                  >
                    <Plus size={14} className="theme-primary-text" /> Agregar Descuento Especial
                  </button>
                ) : (
                  <div className="p-4 bg-white/5 rounded-2xl border border-white/10 space-y-3">
                    <p className="text-[9px] font-black uppercase tracking-widest text-zinc-400 text-left">Nuevo Descuento</p>
                    
                    {/* Days bubbles */}
                    <div className="space-y-1 text-left">
                      <label className="block text-[8px] font-black uppercase tracking-widest text-zinc-500">Días aplicables</label>
                      <div className="grid grid-cols-7 gap-1">
                        {[1, 2, 3, 4, 5, 6, 0].map((dayNum) => {
                          const isSelected = discDays.includes(dayNum);
                          const char = ['D', 'L', 'M', 'M', 'J', 'V', 'S'][dayNum];
                          return (
                            <button
                              key={dayNum}
                              type="button"
                              onClick={() => {
                                if (discDays.includes(dayNum)) {
                                  setDiscDays(discDays.filter(d => d !== dayNum));
                                } else {
                                  setDiscDays([...discDays, dayNum]);
                                }
                              }}
                              className={`py-1 rounded-lg border text-[10px] font-black transition-all ${
                                isSelected
                                  ? 'theme-primary-bg text-black border-transparent scale-105'
                                  : 'bg-[#11131c] border-white/10 text-white/60 hover:bg-white/5'
                              }`}
                            >
                              {char}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Time range */}
                    <div className="grid grid-cols-2 gap-2 text-left">
                      <div className="space-y-1">
                        <label className="block text-[8px] font-black uppercase tracking-widest text-zinc-500">Hora Inicio</label>
                        <input
                          type="time"
                          value={discStart}
                          onChange={(e) => setDiscStart(e.target.value)}
                          style={{ colorScheme: 'dark' }}
                          className="bg-[#11131c] border border-white/10 rounded-lg px-2.5 py-1.5 text-white font-bold text-[10px] w-full outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[8px] font-black uppercase tracking-widest text-zinc-500">Hora Fin</label>
                        <input
                          type="time"
                          value={discEnd}
                          onChange={(e) => setDiscEnd(e.target.value)}
                          style={{ colorScheme: 'dark' }}
                          className="bg-[#11131c] border border-white/10 rounded-lg px-2.5 py-1.5 text-white font-bold text-[10px] w-full outline-none"
                        />
                      </div>
                    </div>

                    {/* Mode & Value */}
                    <div className="grid grid-cols-2 gap-2 text-left">
                      <div className="space-y-1">
                        <label className="block text-[8px] font-black uppercase tracking-widest text-zinc-500">Tipo de Beneficio</label>
                        <select
                          value={discType}
                          onChange={(e) => setDiscType(e.target.value as 'percentage' | 'fixed')}
                          className="bg-[#11131c] border border-white/10 rounded-lg px-2 py-1.5 text-white font-bold text-[10px] w-full outline-none cursor-pointer"
                        >
                          <option value="percentage">Porcentaje (%)</option>
                          <option value="fixed">Monto Fijo ($)</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[8px] font-black uppercase tracking-widest text-zinc-500">
                          {discType === 'percentage' ? 'Valor Descuento (%)' : 'Monto Bonificación ($)'}
                        </label>
                        <input
                          type="number"
                          value={discVal}
                          onChange={(e) => setDiscVal(Number(e.target.value))}
                          placeholder={discType === 'percentage' ? '20' : '5000'}
                          className="bg-[#11131c] border border-white/10 rounded-lg px-2 py-1.5 text-white font-bold text-[10px] w-full outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddDiscount(false);
                          setDiscDays([]);
                        }}
                        className="py-2 bg-white/5 hover:bg-white/10 text-white/60 font-black uppercase text-[9px] rounded-lg transition-all"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (discDays.length === 0) {
                            alert('Selecciona al menos un día aplicable.');
                            return;
                          }
                          const newDisc: TimeDiscount = {
                            id: Math.random().toString(36).substr(2, 9),
                            daysOfWeek: [...discDays].sort(),
                            startHour: discStart,
                            endHour: discEnd,
                            discountType: discType,
                            value: Number(discVal),
                            courts: []
                          };
                          setTimeDiscounts([...timeDiscounts, newDisc]);
                          // Reset form
                          setDiscDays([]);
                          setDiscStart('12:00');
                          setDiscEnd('16:00');
                          setDiscVal(20);
                          setShowAddDiscount(false);
                        }}
                        className="py-2 theme-primary-bg text-black font-black uppercase text-[9px] rounded-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
                      >
                        Agregar
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {step === 5 && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-5 text-center flex flex-col items-center py-2"
              >
                <div className="w-16 h-16 rounded-full bg-theme-primary/10 flex items-center justify-center mb-1 animate-pulse border theme-primary-border">
                  <BellRing className="theme-primary-text animate-bounce" size={28} />
                </div>
                
                <h3 className="text-sm font-black uppercase tracking-wider theme-primary-text">
                  ¡Activa las Notificaciones!
                </h3>

                <p className="text-xs text-white/70 leading-relaxed max-w-sm">
                  Te avisaremos en tiempo real cuando un jugador reserve un turno, cuando se confirme un resultado de partido o cuando recibas nuevos mensajes en el chat de administración.
                </p>

                <div className="w-full space-y-3 pt-4">
                  <button
                    onClick={async () => {
                      await handleSubmit(true);
                    }}
                    disabled={loading}
                    className="w-full theme-primary-bg text-black font-black py-4 rounded-2xl uppercase tracking-[0.2em] text-[10px] shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? 'Guardando configuración...' : 'Activar y Finalizar'} <BellRing size={16} />
                  </button>

                  <button
                    onClick={async () => {
                      await handleSubmit(false);
                    }}
                    disabled={loading}
                    className="w-full bg-white/5 text-white/40 font-black py-3 rounded-xl uppercase tracking-widest text-[9px] hover:bg-white/10 transition-all disabled:opacity-50"
                  >
                    Ahora no, prefiero hacerlo luego
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>

        {/* Footer Actions */}
        {step < 5 && (
          <div className="p-6 pt-0 flex gap-2">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all text-white"
              >
                <ChevronLeft size={18} />
              </button>
            )}

            <div className="flex-1 flex flex-col gap-3">
              {stepError && (
                <div className="text-center animate-pulse">
                  <p className="text-[9px] font-black uppercase tracking-widest text-red-400">
                    ⚠️ {stepError}
                  </p>
                </div>
              )}
              <button
                onClick={() => {
                  setStepError('');
                  if (step === 1) {
                    if (!name.trim()) {
                      setStepError('El Nombre del Club es obligatorio.');
                      return;
                    }
                    if (!city) {
                      setStepError('Debes seleccionar una ciudad.');
                      return;
                    }
                  }
                  setStep(step + 1);
                }}
                className="w-full theme-primary-bg text-black font-black py-4 rounded-xl uppercase tracking-[0.2em] text-[10px] shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                Siguiente <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
